import * as poly2tri from 'poly2tri';
import earcut from 'earcut';
// © 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.

import * as BABYLON from 'babylonjs';
import Hammer from 'hammerjs';
import DisplayManager from '../Managers/DisplayManager';
import MaterialManager from '../Managers/MaterialManager';
import DisplayParser from './DisplayDataParser';
import HighlightEngine from './HighlightEngine';
import AnnotationManager from '../Managers/AnnotationManager';
import SelectionManager from '../Managers/SelectionManager';
import SceneManager from '../Managers/SceneManager';
import MeshManager from '../Managers/MeshManager';
import MeshRepository from './MeshRepository';
import * as Interfaces from '../../Interfaces/Interfaces';
import * as Enumerations from '../../Enumerations/Enumerations';
import MeshFactory from '../workers/MeshFactory';
import EventManager from '../Managers/EventManager';
import VectorUtilities from '../../Utilities/VectorUtilities';
import MeasurementManager from '../Managers/MeasurementManager';
import LabelManager from '../Managers/LabelManager';
import ScanPositionManager from '../Managers/ScanPositionManager';
import GLBManager from '../Managers/GLBManager';
import AssetSetManager from '../Managers/AssetSetManager';
import MaterialOverrideManager from '../Managers/MaterialOverrideManager';
class ViewerEngine {

    
    private _vectorUtilities: VectorUtilities;
    private _markedPointsManager: AnnotationManager;
    private _displayManager: DisplayManager;
    private _meshManager: MeshManager;
    private _selectionManager: SelectionManager;
    private _assetSetManager: AssetSetManager;
    private _highlightEngine: HighlightEngine;
    private _sceneManager: SceneManager;
    private _materialManager: MaterialManager;
    private _2dCanvas: CanvasRenderingContext2D;
    private _meshFactory: MeshFactory;
    private _meshRepository: MeshRepository;
    private _eventManager: EventManager;
    private _measurementManager: MeasurementManager;
    private _labelManager: LabelManager;
    private _scanPositionManager: ScanPositionManager;
    private _assemblyManager: GLBManager;

    constructor(canvas: HTMLCanvasElement, annotationCanvas2D: HTMLCanvasElement, drawCanvas2D: HTMLCanvasElement) {

        if (!("poly2tri" in window)) {
            (window as any).poly2tri = poly2tri;
        }
        if (!("earcut" in window)) {
            (window as any).earcut = earcut;
        }
        var engine = new BABYLON.Engine(canvas as HTMLCanvasElement, true);
        var babScene = new BABYLON.Scene(engine);
        babScene.disablePhysicsEngine();
        
        this._materialManager = new MaterialManager(babScene);
        this._eventManager = new EventManager();
        this._meshManager = new MeshManager(this._eventManager, babScene, this._materialManager);
        this._meshRepository = new MeshRepository(babScene, 1);
        this._highlightEngine = new HighlightEngine(this._meshManager);
    
        this._meshFactory = new MeshFactory(babScene);

        this._labelManager = new LabelManager(babScene, this._eventManager);
        this._measurementManager = new MeasurementManager(babScene, engine);
        this._assemblyManager = new GLBManager(this._eventManager, babScene, this._materialManager);
        this._scanPositionManager = new ScanPositionManager(this._eventManager, babScene);
        this._markedPointsManager = new AnnotationManager((annotationCanvas2D as HTMLCanvasElement).getContext("2d"), this._materialManager, this._meshManager, this._meshFactory, this._eventManager);

        var baseMaterialManager = new MaterialOverrideManager(this._meshManager);
        this._meshManager.setAssetsUnhiddenCallback(assetIds => baseMaterialManager.reapplyAssets(assetIds));

        this._selectionManager = new SelectionManager(this._eventManager, this._meshManager, this._highlightEngine, this._materialManager, this._markedPointsManager, this._labelManager, this._assemblyManager, babScene, baseMaterialManager);
        this._assetSetManager = new AssetSetManager(this._meshManager, this._materialManager, baseMaterialManager);
        this._displayManager = new DisplayManager(this._eventManager, this._meshManager, this._markedPointsManager, this._selectionManager, this._labelManager, this._assemblyManager, this._scanPositionManager, baseMaterialManager, this._assetSetManager);
        this._measurementManager = new MeasurementManager(babScene, engine);
        
        this._sceneManager = new SceneManager(this._eventManager, this._displayManager, this._meshManager, this._selectionManager, this._markedPointsManager, this._materialManager, this._measurementManager, this._labelManager, this._assemblyManager, babScene, (annotationCanvas2D as HTMLCanvasElement), (drawCanvas2D as HTMLCanvasElement), engine);
        this._sceneManager.setLightingMode("ambient");

        this._measurementManager.setRayPickProvider(this._sceneManager);
        this._assemblyManager.setRayPickProvider(this._sceneManager);
    }

    dispose() {
        this._labelManager.dispose();
        this._scanPositionManager.dispose();
        this._assemblyManager.dispose();
        this._measurementManager.dispose();
        this._displayManager.dispose();
        this._meshManager.dispose();
        this._selectionManager.dispose();
        this._highlightEngine.dispose();
        this._sceneManager.dispose();
        this._materialManager.dispose();
        this._meshFactory.dispose();
        this._eventManager.dispose();
        this._markedPointsManager.dispose();
        this._meshRepository.dispose();

        this._labelManager = null;
        this._scanPositionManager = null;
        this._assemblyManager = null;
        this._eventManager = null;
        this._measurementManager = null;
        this._displayManager = null;
        this._meshManager = null;
        this._selectionManager = null;
        this._highlightEngine = null;
        this._sceneManager = null;
        this._materialManager = null;
        this._meshFactory = null;
        this._markedPointsManager = null;
        this._meshRepository = null;
    }

    get VectorUtilities(): Interfaces.Interfaces.IVectorUtilities {
        return this._vectorUtilities;
    }

    get meshManager(): Interfaces.Interfaces.IMeshManager {
        return this._meshManager;
    }

    get materialManager(): Interfaces.Interfaces.IMaterialsManager {
        return this._materialManager;
    }

    get displayManager(): Interfaces.Interfaces.IDisplayManager {
        return this._displayManager;
    }

    get measurementManager(): Interfaces.Interfaces.IMeasurementManager {
        return this._measurementManager;
    }

    get labelManager(): Interfaces.Interfaces.ILabelManager {
        return this._labelManager;
    }

    get scanPositionManager(): Interfaces.Interfaces.IScanPositionManager {
        return this._scanPositionManager;
    }

    get assemblyManager(): Interfaces.Interfaces.IAssemblyManager {
        return this._assemblyManager;
    }

    get sceneManager(): Interfaces.Interfaces.ISceneManager {
        return this._sceneManager;
    }

    get eventManager(): Interfaces.Interfaces.IEventManager {
        return this._eventManager;
    }

    get selectionManager(): Interfaces.Interfaces.ISelectionManager {
        return this._selectionManager;
    }
    get assetSetManager(): Interfaces.Interfaces.IAssetSetManager {
        return this._assetSetManager;
    }

    get markedPointsManager(): Interfaces.Interfaces.IAnnotationManager {
        return this._markedPointsManager;
    }

    clearAllGraphicContent(): void {
        this._selectionManager.unselectAllAssets(true);
        this._measurementManager.clearMeasurements();
        this._displayManager.clearAllContent(false);
        this._markedPointsManager.clearMeasurements();
        this._markedPointsManager.clearAnnotations();
    }
}

export default ViewerEngine;
